import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {user && (
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-rose-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        )}

        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-rose-900 mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600">Find the perfect plan for your journey</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan */}
          <Card className="shadow-xl border-rose-100">
            <CardHeader>
              <CardTitle className="text-3xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>Free</CardTitle>
              <CardDescription className="text-2xl font-bold text-rose-900 mt-2">$0/month</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                <Feature text="5 daily matches" />
                <Feature text="Basic messaging" />
                <Feature text="Profile creation" />
                <Feature text="Compatibility scores" />
                <Feature text="Weekly reflections" />
              </ul>
              <Button
                data-testid="free-plan-btn"
                variant="outline"
                className="w-full border-rose-200"
                onClick={() => user ? navigate('/dashboard') : navigate('/auth')}
              >
                {user ? 'Current Plan' : 'Get Started'}
              </Button>
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <Card className="shadow-2xl border-rose-400 border-2 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-rose-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
              Popular
            </div>
            <CardHeader>
              <CardTitle className="text-3xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>Premium</CardTitle>
              <CardDescription className="text-2xl font-bold text-rose-900 mt-2">$29.99/month</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                <Feature text="Unlimited daily matches" premium />
                <Feature text="Browse & search all users" premium />
                <Feature text="Advanced messaging" premium />
                <Feature text="AI conversation starters" premium />
                <Feature text="AI date ideas" premium />
                <Feature text="Red-flag detection" premium />
                <Feature text="Priority support" premium />
                <Feature text="See who liked you" premium />
              </ul>
              <Button
                data-testid="premium-plan-btn"
                className="w-full bg-rose-500 hover:bg-rose-600"
                onClick={handleSubscribe}
                disabled={loading}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Upgrade to Premium'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const Feature = ({ text, premium }) => (
  <li className="flex items-center gap-2">
    <Check className={`w-5 h-5 flex-shrink-0 ${premium ? 'text-rose-500' : 'text-gray-400'}`} />
    <span className={premium ? 'text-gray-900' : 'text-gray-600'}>{text}</span>
  </li>
);

export default PricingPage;